import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import flagsmith from "@flagsmith/flagsmith";
import { useTenant } from "../branding/useTenant";
import { FeatureFlagName, type FeatureFlags } from "./featureFlags";
import { loadFlagBackup } from "./flagBackup";
import { flagsmithIdentity, resolveFlagsmithSettings, utilityIdForKey } from "./flagsmithConfig";

interface FeatureFlagsContextValue {
  flags: FeatureFlags | undefined;
  isLoading: boolean;
  /** Re-read the flags from Flagsmith now; resolves once the SDK has answered. */
  refresh: () => Promise<void>;
}

/** How long the first load waits for Flagsmith before falling back to the backup. */
const FLAGSMITH_TIMEOUT_MS = 5000;

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: undefined,
  isLoading: false,
  refresh: () => Promise.resolve(),
});

/**
 * Flagsmith stores flags lower-cased, but hasFeature() applies the same normalisation
 * on lookup — so reading through it keeps the PascalCase keys consumers index by
 * (QuickServices does `flags[FeatureFlagName.SERVICE_REQUEST]` directly).
 *
 * Only flags declared in FeatureFlagName appear in the map, which is already the app's
 * contract: an undeclared flag has nothing reading it.
 */
function readFlags(): FeatureFlags {
  return Object.fromEntries(
    Object.values(FeatureFlagName).map(name => [name, flagsmith.hasFeature(name)]),
  );
}

/**
 * Resolves the current tenant's feature flags through the Flagsmith client SDK and
 * shares them via context. The SDK talks to the Edge Proxy directly — there is no Xepp
 * endpoint in this path.
 *
 * Targeting is by the `utilityId` trait, matched by a Flagsmith segment, so the tenant
 * change (host pre-login → JWT after login) is a re-identify rather than a refetch.
 *
 * Where to connect (proxy URL, client key, backup container) comes from the branding
 * response first and the build second — see resolveFlagsmithSettings. init() therefore
 * waits for branding to answer, which costs nothing visible: TenantProvider already holds
 * the app behind BrandingLoadingDialog meanwhile. The SDK's key and URL are fixed at
 * init(), so the values current at that moment are the ones used for the session.
 *
 * If Flagsmith fails, times out or answers with no flags, the utility's backup snapshot
 * is used instead (see flagBackup.ts). With no usable backup either the flags stay
 * undefined, which reads as everything off. Nothing about the backup is kept, so the next
 * load asks Flagsmith first again.
 */
export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { key, featureFlags: backendSettings } = useTenant();
  const [flags, setFlags] = useState<FeatureFlags>();
  const [isLoading, setIsLoading] = useState(true);
  const init = useRef<Promise<void> | null>(null);
  // Whether live or backup flags have been applied - the fallback only fills an empty map.
  const hasFlags = useRef(false);

  // Keyed on the resolved id rather than the tenant key: post-login both describe the
  // same utility, so re-running on `key` alone would re-identify for no reason.
  const utilityId = utilityIdForKey(key);
  // For refresh(), which must target the utility current at call time, not at creation.
  const utilityIdRef = useRef(utilityId);
  utilityIdRef.current = utilityId;

  // undefined while branding has not answered yet - see TenantBrand.featureFlags.
  const settings = useMemo(
    () => (backendSettings === undefined ? undefined : resolveFlagsmithSettings(backendSettings)),
    [backendSettings],
  );
  // For the fallback, which reads the backup URL at the moment it fires, not at init().
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  // The effect keys on the two values init() is fixed to, not on the settings object, so a
  // branding refetch that returns the same values does not re-identify for nothing.
  const environmentID = settings?.environmentID;
  const api = settings?.api;

  useEffect(() => {
    // No utility to target — the BPS admin portal. It has no flags, so there is nothing
    // to ask Flagsmith for; an empty map reads as everything off.
    if (utilityId === null) {
      setFlags({});
      setIsLoading(false);
      return;
    }

    // Branding still pending: wait for its settings rather than initialise with the build's
    // and learn the backend's a moment later, when the SDK can no longer take them.
    if (environmentID === undefined || api === undefined) return;

    // No key from the backend and none in the build: nothing to ask Flagsmith with. Off
    // everywhere, deterministically, rather than a 401 that the fallback would then mask.
    if (!environmentID) {
      if (!init.current) {
        console.warn("Flagsmith: no client-side key from the backend or the build; every feature flag reads as off.");
        setFlags({});
        setIsLoading(false);
      }
      return;
    }

    const identity = flagsmithIdentity();
    const traits = { utilityId };

    if (!init.current) {
      // Only while nothing has loaded: a later failure (a refresh) keeps the flags it has,
      // and the backup is fetched at most once per page load.
      let fellBack = false;
      const fallBack = () => {
        if (fellBack || hasFlags.current) return;
        fellBack = true;
        void loadFlagBackup(utilityId, settingsRef.current?.backupUrl ?? "").then(backup => {
          // Live flags that landed while the backup was on its way win.
          if (backup && !hasFlags.current) {
            hasFlags.current = true;
            setFlags(backup);
          }
          setIsLoading(false);
        });
      };
      // The SDK has no timeout of its own, and a proxy that never answers would otherwise
      // keep every FeatureGuard-ed page blank.
      const timeout = setTimeout(fallBack, FLAGSMITH_TIMEOUT_MS);

      init.current = flagsmith
        .init({
          environmentID,
          api,
          headers: settingsRef.current?.headers,
          identity,
          traits,
          // Deliberately off. The SDK's cache key is per environment, not per identity,
          // and `storageKey` is only read at init() — so it cannot follow a utility
          // change, and the next load would paint the previous tenant's flags before
          // correcting itself. Flags stay undefined until the fetch lands, which reads
          // as everything off: the safe direction, and what the app did before.
          cacheFlags: false,
          // The Edge Proxy has no analytics endpoint, and we do not use the data.
          enableAnalytics: false,
          onChange: () => {
            // An answer with no flags at all is Flagsmith failing, not a utility that has
            // everything off.
            if (Object.keys(flagsmith.getAllFlags()).length === 0) {
              fallBack();
              return;
            }
            clearTimeout(timeout);
            // Also replaces a backup applied while this was still on its way.
            hasFlags.current = true;
            setFlags(readFlags());
            setIsLoading(false);
          },
          onError: fallBack,
        })
        .catch(fallBack);
      return;
    }

    // Wait for the first load, otherwise a change during startup races init().
    init.current.then(() => flagsmith.identify(identity, traits));
  }, [utilityId, environmentID, api]);

  // The SDK fetches on init() and never again on its own — no polling. Consumers that
  // must not show stale values (the util-admin module selection, a tab coming back to
  // the foreground) ask for a re-read. Waits for init() so a call during startup
  // cannot race the first load; the result lands through onChange like any other fetch.
  // The current flags stay in place meanwhile, so a refresh never flashes "everything
  // off" the way the initial load does.
  //
  // Re-identify rather than getFlags(), so every re-read carries our `utilityId` trait
  // exactly as we spell it instead of whatever the SDK kept from the last response.
  // The legacy `flagsmith@10` package lower-cased stored trait keys, so a plain
  // getFlags() re-sent `utilityid`, matched no segment (keys compare case-sensitively)
  // and every flag fell back to its default — all module cards locked after a
  // back-navigation. `@flagsmith/flagsmith@12` keeps the key as sent, but asserting
  // the trait ourselves keeps the refresh independent of that detail.
  const refresh = useCallback(async () => {
    if (!init.current || utilityIdRef.current === null) return;
    await init.current;
    // A failed re-read keeps the last known flags; the SDK has already reported it
    // through onError, so there is nothing further to surface here.
    await flagsmith
      .identify(flagsmithIdentity(), { utilityId: utilityIdRef.current })
      .catch(() => undefined);
  }, []);

  // No refresh when a tab is left open — re-read whenever the user comes back to it.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  return (
    <FeatureFlagsContext.Provider value={{ flags, isLoading, refresh }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};
